/**
 * @file guards.ts
 * @description 路由守卫集合
 *
 * 将 router/index.ts 中的 beforeEach / afterEach 校验逻辑按职责拆分到此文件，
 * 保持路由配置与导航守卫职责分明。
 */

import type { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import { useDeviceStore } from '@/stores'
import { useAdminOnly } from '@/composables/useAdminOnly'
import { storeToRefs } from 'pinia'

/** beforeEach 守卫：设备类型校验 */
export function deviceTypeGuard(
  to: RouteLocationNormalized,
  _: RouteLocationNormalized,
  next: NavigationGuardNext,
): void {
  if (to.meta && to.meta?.type) {
    const deviceStore = useDeviceStore()
    const { currentDevice } = storeToRefs(deviceStore)
    const currentType = currentDevice.value?.manifest.type
    if (currentType !== to.meta.type) {
      next('/')
      return
    }
  }
  next()
}

/** beforeEach 守卫：admin-only 页面权限校验 */
export function adminOnlyGuard(
  to: RouteLocationNormalized,
  _: RouteLocationNormalized,
  next: NavigationGuardNext,
): void {
  if (to.meta?.adminOnly) {
    const { canShowBaseScreenContent } = useAdminOnly()
    if (!canShowBaseScreenContent.value) {
      next({ name: 'base-light' })
      return
    }
  }
  next()
}

/**
 * beforeEach 守卫：键盘改键 / 行程可见性校验。
 * 与 keyboard.ts beforeEnter 同源（useAdminOnly），覆盖直链与登录后仍停在受限页的情况。
 */
export function keyboardFeatureGuard(
  to: RouteLocationNormalized,
  _: RouteLocationNormalized,
  next: NavigationGuardNext,
): void {
  if (to.name === 'keyboard-keys') {
    const { canShowKeyboardKeys } = useAdminOnly()
    if (!canShowKeyboardKeys.value) {
      next({ name: 'keyboard-light' })
      return
    }
  }
  if (to.name === 'keyboard-itinerary') {
    const { canShowKeyboardItinerary } = useAdminOnly()
    if (!canShowKeyboardItinerary.value) {
      next({ name: 'keyboard-light' })
      return
    }
  }
  next()
}
